import { QUARTERZIP_LOGO_SVG } from './logo';

export const PANEL_BG = '#262626'; // --color-gray-800: rgba(38, 38, 38, 1)
const LOGO_COLOUR = '#fafafa'; // .dark --foreground: oklch(0.985 0 0)

const LOGO_SIZE_PX = 35;
const CAPTION_GAP_PX = 8;
const CAPTION_HEIGHT_PX = 20;

const FADE_MS = 250;

const PULSE_KEYFRAMES: Keyframe[] = [
	{ transform: 'scale(0.9)', opacity: 0.6 },
	{ transform: 'scale(1.1)', opacity: 1 },
	{ transform: 'scale(0.9)', opacity: 0.6 }
];
const PULSE_OPTIONS: KeyframeAnimationOptions = {
	duration: 2000,
	easing: 'cubic-bezier(0.4, 0, 0.6, 1)',
	iterations: Infinity
};

/** Styles are set through CSSOM, not a `style` attribute, so a strict `style-src-attr` CSP allows them. */
const div = (doc: Document, styles: Partial<CSSStyleDeclaration>): HTMLDivElement => {
	const el = doc.createElement('div');
	Object.assign(el.style, styles);
	return el;
};

const spacer = (doc: Document, heightPx: number): HTMLDivElement =>
	div(doc, { flex: '0 0 auto', height: `${heightPx}px` });

/** The PiP window's placeholder only — the call frame uses `LoadingPuck`. */
export class LoadingPlaceholder {
	readonly element: HTMLDivElement;

	private animation: Animation | null = null;
	private fadeTimer: ReturnType<typeof setTimeout> | null = null;
	private dismissed = false;

	constructor(doc: Document) {
		const root = div(doc, {
			position: 'fixed',
			inset: '0',
			boxSizing: 'border-box',
			display: 'flex',
			flexDirection: 'column',
			background: PANEL_BG,
			// Never intercept a click meant for the host page or the frame above.
			pointerEvents: 'none',
			opacity: '1',
			transition: `opacity ${FADE_MS}ms ease-out`
		});

		// Mirrors LoadingPanel's own centring classes, so the browser does the arithmetic.
		const body = div(doc, {
			flex: '1 1 auto',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			gap: `${CAPTION_GAP_PX}px`,
			// The user can shrink the PiP window below the content's natural size.
			overflow: 'hidden'
		});

		const mark = div(doc, {
			flex: '0 0 auto',
			display: 'flex',
			width: `${LOGO_SIZE_PX}px`,
			height: `${LOGO_SIZE_PX}px`,
			color: LOGO_COLOUR
		});
		mark.innerHTML = QUARTERZIP_LOGO_SVG;

		body.appendChild(mark);
		body.appendChild(spacer(doc, CAPTION_HEIGHT_PX));
		root.appendChild(body);

		this.element = root;
		// Web Animations rather than a keyframes stylesheet: injecting a <style> into the host page
		// would be blocked by a strict `style-src` CSP, and would leak a global @keyframes name.
		this.animation = mark.animate(PULSE_KEYFRAMES, PULSE_OPTIONS);
	}

	dismiss(): void {
		if (this.dismissed) return;
		this.dismissed = true;
		this.element.style.opacity = '0';
		this.fadeTimer = setTimeout(() => this.destroy(), FADE_MS);
	}

	destroy(): void {
		if (this.fadeTimer) {
			clearTimeout(this.fadeTimer);
			this.fadeTimer = null;
		}
		this.animation?.cancel();
		this.animation = null;
		this.element.remove();
	}
}
